import { appConfig } from "src/appConfig";
import { AppContextState } from "src/AppContextProvider";
import { Plan } from "src/billing/types";
import { getPlanNameWithoutCurrency } from "src/helpers/planHelper";

type Mixpanel = (typeof import("mixpanel-browser"))["default"];

/**
 * mixpanel-browser is ~300 KB and analytics don't need to block start-up, so the library is
 * loaded on first use. init() and track() both chain onto the same promise, so they still run
 * in the order they were called. A failed load is ignored (analytics must never break the app)
 * and retried on the next call.
 */
let mixpanelLoader: Promise<Mixpanel> | undefined;

const withMixpanel = (run: (mixpanel: Mixpanel) => void): void => {
  if (!mixpanelLoader) {
    mixpanelLoader = import("mixpanel-browser").then(
      (module) => module.default,
    );
  }
  const loader = mixpanelLoader;
  loader.then(run).catch(() => {
    if (mixpanelLoader === loader) {
      mixpanelLoader = undefined;
    }
  });
};

export const shouldDoMixpanel = () => {
  return (
    appConfig.studioPlayerEnvironment !== "development" &&
    appConfig.studioPlayerEnvironment !== "edge"
  );
};

export const getMixpanelPlanTier = (
  context: AppContextState,
): string | undefined => {
  const subscription = context.subscription;
  if (!subscription) {
    return undefined;
  }

  if (
    "billingPlanByPlanId" in subscription &&
    subscription.billingPlanByPlanId
  ) {
    const plan = subscription.billingPlanByPlanId as Pick<
      Plan,
      "cfName" | "cfIncludedPhysicalScreenLicenses" | "name"
    >;
    return getPlanNameWithoutCurrency(plan);
  }

  if ("planId" in subscription && subscription.planId) {
    const tier = subscription.planId.split("-")[0];
    return `${tier.charAt(0).toUpperCase()}${tier.slice(1)}`;
  }

  return undefined;
};

export const getMixpanelDaysIntoTrial = (
  context: AppContextState,
): number | undefined => {
  const subscription = context.subscription;
  if (
    !subscription ||
    !("trialStart" in subscription) ||
    !subscription.trialStart
  ) {
    return undefined;
  }

  return Math.floor((Date.now() / 1000 - subscription.trialStart) / 86400);
};

export const getPublishUsageMetrics = ({
  createdAt,
  publishedAt,
  itemCount,
}: {
  createdAt?: string | null;
  publishedAt?: string | null;
  itemCount: number;
}) => {
  const now = Date.now();
  const createdTime = createdAt ? new Date(createdAt).getTime() : null;
  const lastPublishedTime = publishedAt
    ? new Date(publishedAt).getTime()
    : null;

  return {
    item_count: itemCount,
    minutes_to_publish:
      createdTime != null ? Math.round((now - createdTime) / 60000) : undefined,
    is_republish: lastPublishedTime != null,
    days_since_last_publish:
      lastPublishedTime != null
        ? Math.round((now - lastPublishedTime) / 86400000)
        : undefined,
  };
};

export type PairingCodeSource = "url" | "prop";

export const getPairingCodeTrackingProperties = ({
  usedUrlPrefill,
  defaultPairingCode,
  pairingPath,
}: {
  usedUrlPrefill: boolean;
  defaultPairingCode?: string;
  pairingPath?: string;
}): Record<string, string> => {
  const properties: Record<string, string> = {};

  if (usedUrlPrefill) {
    properties.pairing_method = "prefilled_code";
    properties.pairing_code_source = "url";
  } else if (defaultPairingCode) {
    properties.pairing_method = "prefilled_code";
    properties.pairing_code_source = "prop";
  } else {
    properties.pairing_method = "manual_code";
  }

  if (pairingPath) {
    properties.pairing_path = pairingPath;
  }

  return properties;
};

export const getPairingStartedTrackingProperties = (
  pairingPath: string,
): Record<string, string> => ({
  pairing_path: pairingPath,
});

export const ONBOARDING_STEPS = [
  "design_screen_content",
  "pair_screen",
  "set_content_to_screen",
] as const;

export type OnboardingStepName = (typeof ONBOARDING_STEPS)[number];

export type StudioOnboardingFlow = "standard" | "pixi_bundle";

const TOTAL_ONBOARDING_STEPS = ONBOARDING_STEPS.length;

export type OnboardingStepEntryPoint = "create_content" | "upload_media";

export const trackOnboardingStepEvent = ({
  context,
  status,
  stepName,
  stepNumber,
  onboardingFlow,
  entryPoint,
}: {
  context: AppContextState;
  status: "started" | "completed";
  stepName: OnboardingStepName;
  stepNumber: number;
  onboardingFlow: StudioOnboardingFlow;
  /**
   * Which control the user took into the step, for steps that now offer more
   * than one entry point. `step_name` stays stable so historical analytics
   * remain comparable (GRWBKK-2026).
   */
  entryPoint?: OnboardingStepEntryPoint;
}) => {
  trackMixpanelEvent({
    event:
      status === "started"
        ? "Studio Onboarding Step Started"
        : "Studio Onboarding Step Completed",
    context,
    properties: {
      onboarding_flow: onboardingFlow,
      step_name: stepName,
      step_number: stepNumber,
      ...(entryPoint ? { entry_point: entryPoint } : {}),
    },
  });

  const prefs = context.currentOrg?.preferences.pixi_trialist;
  const shouldTrackFlowCompletion =
    status === "completed" &&
    stepNumber === TOTAL_ONBOARDING_STEPS &&
    prefs?.completed_design_screen_content === true &&
    prefs?.completed_pair_screen === true;

  if (shouldTrackFlowCompletion) {
    trackMixpanelEvent({
      event: "Studio Onboarding Completed",
      context,
      properties: {
        onboarding_flow: onboardingFlow,
        total_steps: TOTAL_ONBOARDING_STEPS,
      },
    });
  }
};

export type OnboardingPlanSelectionType = "plan" | "billing_cycle" | "deal";

export const trackOnboardingPlanSelectionEvent = ({
  context,
  status,
  planTier,
  billingCycle,
  deal,
  licenses,
  shippingCountry,
  selectionType,
  selectionValue,
}: {
  context: AppContextState;
  status: "selected" | "completed";
  planTier: string;
  billingCycle: string;
  deal?: string;
  licenses: number;
  shippingCountry?: string;
  selectionType?: OnboardingPlanSelectionType;
  selectionValue?: string;
}) => {
  trackMixpanelEvent({
    event:
      status === "selected"
        ? "Studio Onboarding Plan Option Selected"
        : "Studio Onboarding Plan Selection Completed",
    context,
    properties: {
      onboarding_flow: "pixi_bundle",
      plan_tier: planTier,
      billing_cycle: billingCycle,
      deal: deal ?? "none",
      licenses,
      ...(shippingCountry !== undefined && {
        shipping_country: shippingCountry,
      }),
      ...(selectionType !== undefined && {
        selection_type: selectionType,
      }),
      ...(selectionValue !== undefined && {
        selection_value: selectionValue,
      }),
    },
  });
};

export const initMixpanel = (context: AppContextState) => {
  if (shouldDoMixpanel()) {
    withMixpanel((mixpanel) =>
      mixpanel.init(appConfig.mixpanelToken, {
        autocapture: false,
        debug: true,
      }),
    );
    // as we init mixpanel once user login, so we can just track the event once here too
    trackMixpanelEvent({
      event: "User Login",
      context,
    });
  }
};

export const trackMixpanelEvent = ({
  event,
  properties,
  context,
}: {
  event: string;
  properties?: Record<string, any>;
  context: AppContextState;
}) => {
  if (shouldDoMixpanel()) {
    // read the context now, not when the library has finished loading
    const eventProperties = {
      ...properties,
      distinct_id: context.currentUser?.id,
      org_id: context.currentOrg?.id,
      env: appConfig.studioPlayerEnvironment,
    };
    withMixpanel((mixpanel) => mixpanel.track(event, eventProperties));
  }
};
